/** Shared UI strings (shell, states, buttons). The Vietnamese file must have exactly the same keys. */
export const common = {
  app: { name: 'Registrar Control Center', short: 'Control Center', skip: 'Skip to content' },
  env: { mock: 'Mock data', mockHint: 'Sample data generated in the browser, not real records' },
  /** The server this Control Center runs on (ADR-018): the OT&E (test) server or production. */
  deployment: {
    ote: 'OT&E',
    production: 'Production',
    label: 'Server: {{env}}',
    banner: 'Test environment (OT&E)',
    bannerDetail: 'This is the test server: its data is test data and registry commands go to the registries’ test systems.',
  },
  sidebar: { toggle: 'Toggle sidebar', title: 'Navigation', description: 'Main navigation of the console.' },
  search: {
    placeholder: 'Search domain, ID, clTRID/svTRID, request or trace ID…',
    label: 'Global search',
    looksLike: 'Looks like:',
    go: 'Search',
    commandTitle: 'Go to',
    commandPlaceholder: 'Type a page name or a search query…',
    commandEmpty: 'No page matches.',
    searchFor: 'Search for “{{q}}”',
    pages: 'Pages',
  },
  user: {
    operator: 'Operator',
    signedIn: 'Signed-in operator',
    user: 'User',
    loadFailed: 'Could not load your profile',
    mfaVerified: 'MFA verified',
    noMfa: 'No MFA',
    noMfaHint: 'Sensitive actions (disputes, disclosure decisions and releases) need an MFA-verified session',
    expires: 'Session expires {{at}}',
    reauth: 'Sign in again with TOTP',
    signOut: 'Sign out',
    signingOut: 'Signing out…',
    roles: 'Roles',
  },
  theme: { label: 'Theme', light: 'Light', dark: 'Dark', system: 'System' },
  lang: { label: 'Language', vi: 'Tiếng Việt', en: 'English' },
  nav: { open: 'Open navigation', close: 'Close navigation', collapse: 'Collapse sidebar', expand: 'Expand sidebar', home: 'Home' },
  state: {
    loading: 'Loading…',
    empty: 'Nothing here yet',
    error: 'Something went wrong',
    retry: 'Retry',
    code: 'Code',
    requestId: 'Request ID',
    copy: 'Copy {{value}}',
    copied: 'Copied',
    unknown: 'Unknown',
    none: '—',
  },
  action: {
    refresh: 'Refresh',
    cancel: 'Cancel',
    save: 'Save',
    submit: 'Submit',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    older: 'Older',
    newest: 'Newest',
    apply: 'Apply',
    clear: 'Clear',
    reset: 'Reset filters',
    view: 'View',
    open: 'Open',
    filter: 'Filter',
    all: 'All',
    more: 'More',
    export: 'Export',
  },
  table: { rows: '{{count}} rows', showing: 'Showing {{count}}', noResults: 'No results match these filters.' },
  time: { generated: 'Generated {{at}}', updated: 'Updated {{at}}', ago: '{{value}} ago', in: 'in {{value}}' },
  placeholder: {
    title: 'Coming in phase {{phase}}',
    body: 'This page is planned for phase {{phase}} ({{name}}).',
  },
  errors: {
    notFound: 'Page not found',
    notFoundBody: 'There is no page at this address.',
    goHome: 'Go to the home page',
    routeError: 'This page failed to render',
    nothingAt: 'Nothing lives at {{path}}. Check the address, or use the search box or the menu to find what you need.',
    crashed: 'This page crashed',
    crashedHint: 'The rest of the console keeps working. Reload the page; if it keeps failing, report it with the request ID.',
    reload: 'Reload page',
  },
  phase: {
    title: '{{what}} is planned for phase {{phase}}: {{name}}',
    thisPage: 'This page',
    scope: 'Phase {{phase}} scope: {{summary}}',
    filters: 'It will open with these filters applied:',
    overview: 'Operations overview',
    domains: 'Domains',
    names: {
      '0': 'Foundation',
      '1': 'Customer / Subject / Dossier',
      '2': 'Registry / EPP',
      '3': 'Domain',
      '4': 'Commerce',
      '5': 'VNNIC',
      '6': 'ICANN / Registration Data',
      '7': 'Transfer / Expiry / Restore',
      '8': 'Auction',
      '9': 'Backorder / Drop Catch',
      '10': 'Full Control Center / Observability',
      '11': 'Hardening',
    },
    summaries: {
      '0': 'Platform foundation: workflows, audit log and history, policy engine, simulated registry and the Control Center shell.',
      '1': 'Customers, subjects and versions, documents, identity verification, dossiers.',
      '2': 'EPP, registry connections, registry transactions, poll messages, reconciliation.',
      '3': 'Domains, lifecycle, registration, renewal, nameservers/hosts, DNSSEC, history, timeline.',
      '4': 'Registry prices, price books, quotes, orders, ledger, holds, payments, invoices/refunds.',
      '5': 'VNNIC compliance hub, dossier sync, reporting data, 0805A/0805B, reconciliation.',
      '6': 'RDAP, WHOIS, publication policy, disclosure, RDE, abuse, disputes.',
      '7': 'Transfer in/out, registrant change, expiration policy, grace/redemption, restore/delete.',
      '8': 'Auctions, serialized bids, holds, settlement, realtime display.',
      '9': 'Backorders, priority rules, dedicated EPP capacity, settlement.',
      '10': 'Metrics, logs, traces, alerts, incidents, runbooks and the links between them.',
      '11': 'Load, chaos, HA/DR, security and privacy review, OT&E/conformance.',
    },
  },
  login: {
    docTitle: 'Sign in',
    subtitle: 'Staff sign-in',
    intro: 'Sign in with your work account to manage domains, registries and compliance.',
    email: 'Work email',
    password: 'Password',
    totp: 'Authenticator code (TOTP)',
    totpHint: 'Required for accounts with MFA enforced. Without it you get a session that cannot perform sensitive actions (disputes, disclosure decisions and releases).',
    submit: 'Sign in',
    submitting: 'Signing in…',
    expired: 'Your session ended or was rejected by the server. Sign in again.',
    needsMfa: 'The action you tried needs an MFA-verified session. Sign in with your authenticator code.',
    mock: 'Sample data mode: sign-in is simulated. Any email and a password of 8+ characters work; add any 6-digit code for an MFA-verified session. An email starting with {{locked}} shows the lockout error; one starting with “customer” signs in as a customer user.',
    failed: 'Sign-in failed',
    requestFailed: 'The request failed.',
    invalidTitle: 'Invalid credentials',
    invalidBody: 'The email, password or authenticator code is wrong. Accounts with MFA enforced must enter the current 6-digit code; without it the sign-in is refused with this same message.',
    lockedTitle: 'Account temporarily locked',
    lockedBody: 'Too many failed attempts, or the account is not active. Wait for the lockout to expire or contact an administrator.',
    networkTitle: 'Cannot reach the server',
    networkBody: 'Check your network connection and try again. If it keeps failing, contact the platform team.',
    otherBody: '{{message}} ({{code}}, request {{requestId}})',
    footer: 'Every sign-in is recorded in the audit log.',
    demo: 'Quick sign-in (OT&E)',
    demoHint: 'Test server: signs you in as {{email}} straight away, without a password or code.',
    demoLimited: 'Too many quick sign-ins from this address. Wait a minute and try again.',
  },
  searchPage: {
    title: 'Search',
    docTitle: 'Search: {{q}}',
    subtitle: 'Look up any domain or identifier across the platform.',
    empty: 'Type in the search box above.',
    interpreted: 'Interpreted as',
    confidence: { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence' },
    results: 'Results',
    searching: 'Searching…',
    failed: 'Search failed',
    noMatches: 'No matches',
    noMatchesBody: 'Nothing matched {{q}}. IDs, clTRIDs and svTRIDs must match exactly; domain names match the exact name.',
    correlation: 'If this is a correlation ID, the {{link}} shows every event, workflow, registry transaction and audit row it touched.',
    correlationLink: 'Correlation view',
    accepts: 'Search accepts',
    acceptsList: {
      domains: 'Domain names, in Unicode (ví-dụ.vn) or Punycode (xn--v-d-rma6749a.vn)',
      uuids: 'UUIDs (entity, request, correlation, workflow IDs; UUIDv7 shows when it was minted) and ROIDs',
      trids: 'EPP clTRID / svTRID',
      ids: 'Request, correlation, trace (32 hex or traceparent) and workflow IDs',
      parties: 'Customer and subject IDs',
    },
    shortcut: 'Press {{key}} anywhere to focus the search box.',
    kinds: {
      domain: 'Domain',
      domain_event: 'Event',
      registry_transaction: 'Registry tx',
      workflow: 'Workflow',
      subject: 'Subject',
      customer: 'Customer',
    },
    hit: {
      domain: 'Domain',
      domainState: 'Domain · {{state}}',
      event: 'Domain history event (matched by request or correlation ID)',
      registryTx: 'Registry transaction',
      workflow: 'Workflow',
      subject: 'Subject ({{kind}})',
      customer: 'Customer {{id}}',
    },
    /** Readings of the query (classifyQuery), keyed by kind. */
    cls: {
      traceparent: 'W3C traceparent',
      traceparentDetail: 'Trace ID {{id}}',
      uuid: 'UUID (v{{version}})',
      uuidDetail: 'entity, request, correlation, workflow or registry-transaction ID',
      uuidMinted: 'minted {{at}} · entity, request, correlation, workflow or registry-transaction ID',
      traceId: 'Trace ID',
      traceIdDetail: '128-bit W3C trace ID',
      email: 'Email address',
      emailDetail: 'Customer or contact lookup',
      domain: 'Domain name',
      domainIdnUnicode: 'Domain name (Unicode IDN)',
      domainIdnPunycode: 'Domain name (Punycode IDN)',
      trid: 'EPP transaction ID (clTRID / svTRID)',
      tridDetail: 'Client or server transaction identifier from an EPP exchange',
      roid: 'Registry object ID (ROID)',
      roidDetail: 'Repository ID assigned by the registry',
      numeric: 'Numeric reference',
      numericDetail: 'Order, invoice or customer number',
      freeText: 'Free text',
      freeTextDetail: 'Name search across domains, customers and subjects',
    },
    actions: {
      openTrace: 'Open trace',
      logsByRequest: 'Search logs by request ID',
      openWorkflow: 'Open as workflow',
      openRegistryTx: 'Open as registry transaction',
      customers: 'Customers',
      openDomain: 'Open domain',
      findRegistryTx: 'Find registry transaction',
      domains: 'Domains',
      orders: 'Orders',
      invoices: 'Invoices',
    },
  },
  // Reasons the platform writes on records (lib/systemReason.ts).
  systemReason: {
    reportingRestart: 'The reporting consumer restarted the sync',
    reportingEpochChanged: 'The consumer changed its sync epoch; the sync starts again',
    reportingProjectionChanged: 'The feed projection changed; the sync starts again',
    reportingWeeklyResweep: 'Weekly re-sweep',
    reportingSuspended: 'Reporting key suspended',
    reportingExposedInUrl: 'Reporting key exposed in a URL',
    reportingBudgetExceeded: 'Reporting query budget exceeded',
    reportingNetworkNotAllowed: 'Called from a network that is not allowed',
    postRegistrationLock: 'Transfer lock after registration (transfer policy)',
    postTransferLock: 'Transfer lock after a transfer in (transfer policy)',
    changeOfRegistrantLock: 'Transfer lock after a change of registrant (transfer policy)',
    registrarLock: 'Transfer lock set by the registrant or staff',
    unlockedByRegistrant: 'Unlocked by the registrant',
    lockExpired: 'Time-boxed lock expired',
    nameChangeLock: 'Name change completed: full client lock (as VNNIC does after registration)',
    registryLockRemoved: 'Registry lock removed at the registry',
    confirmedByStaff: 'Confirmed by staff',
    checkAtRegistry: 'Check again at the registry',
    registryShowsChange: 'The registry shows the change (domain:info)',
    nameChangeCancelled: 'Name-change request cancelled',
    registrationCancelled: 'Registration cancelled by staff',
    registrationResumed: 'Registration resumed by staff',
    registrationReactivated: 'Registration order reactivated by staff',
    registrationEndedUncreated: 'The registration ended without the domain being created',
    domainRegistered: 'Domain registered',
    domainDeleted: 'The domain was deleted',
    upstreamNoLonger: 'The upstream registrar no longer reports it',
    submittedInPanel: 'Submitted in the VNNIC panel',
    carryIdentity: 'Unchanged identity data carried into a new subject version',
    carryAccountIdentity: "The former account's identity data carried into the primary subject",
    carryCustomerIdentity: 'Identity data carried over from the customer account',
    ekycComparison: 'eKYC (OCR) compared with the declared subject data',
    ekycVerification: 'Automatic eKYC verification (VNNIC OCR / FaceID)',
    documentVerification: 'Automatic document check',
    hstmBackup: 'Backup of the dossier sent to VNNIC (HSTM)',
    businessRegistryLookup: 'Business registry lookup through VNNIC',
    promotionCodeRequest: 'Promotion code request to VNNIC',
    declaration: 'Declaration',
    declarationRepresentative: 'Declaration: legal representative',
    declarationCheckGenerated: 'Declaration check: the generated PDF',
    declarationCheckUploaded: 'Declaration check: the uploaded file',
    declarationDownload: 'Declaration downloaded by its registrant',
    dossierFileSignatureCheck: 'Dossier file: signature check',
    registrarSignature: "The registrar signs the dossier's file",
    signatureBackfill: 'Registrar signature backfill: the signed declaration and the file it replaced',
    proofEkyc: 'Subject data from an eKYC result',
    proofDigitalSignature: 'Subject data from a digitally signed file whose signature was checked',
    acceptedWhenSigning: 'Accepted by the registrar when signing',
    declarationSuperseded: 'Replaced by a new upload of the declaration',
    declarationSignedManual: 'The registrar signed the domain declaration (manual request)',
    dossierPassesChecks: 'The dossier now passes the checks before signing',
    signatureRecorded: "The registrar's signature was recorded as the confirmation",
    problemGone: 'The underlying problem is no longer present',
    dossierClosed: 'The dossier was completed or superseded, or the domain is closed',
    notificationResent: 'The notification was sent again',
    einvoiceIssued: 'The e-invoice was issued',
    contentChangedAfterCorrection: 'Content changed after a correction request',
    changeOfRegistrant: 'Change of registrant',
    registrantCorrected: 'Registrant data corrected before the registry create (staff retry)',
    primarySubjectCreated: 'Primary subject created with the customer',
    representativeWithCustomer: 'Legal representative entered with the new customer',
    representativeOnForm: 'Legal representative set on the subject form',
    signInCreated: 'Sign-in created with the account',
    badPasswordOrCode: 'Wrong password or code',
    unknownAccount: 'Unknown account',
    lockedOrInactive: 'Account locked or inactive',
    mfaRequired: 'Two-factor authentication required',
    mfa: 'Signed in with two-factor authentication',
    passwordOnly: 'Signed in with a password only',
    quickSignIn: 'OT&E quick sign-in, without password or code',
    quickSignInMfa: 'OT&E quick sign-in, session counted as two-factor verified',
    wrongEmailOrPassword: 'Wrong e-mail or password',
    wrongCode: 'Wrong code',
    tooManyWrongPasswords: 'Too many wrong passwords',
    tooManyFromAddress: 'Too many attempts from this address',
    wrongDomainOrDnsPassword: 'Wrong domain or DNS password',
    tooManyWrongDnsPasswords: 'Too many wrong DNS passwords from this address',
    emailNotVerified: 'E-mail not verified',
    notAMember: 'The user is not a member of this account',
    signedOut: 'Signed out',
    captchaInvalid: 'Anti-robot check result not valid',
    captchaExpiredOrUsed: 'Anti-robot check result expired or already used',
    captchaWrongSite: 'Anti-robot check done on another site',
    captchaWrongForm: 'Anti-robot check done for another form',
    registryRejectedRenewal: 'The registry rejected the renewal',
    paymentSettled: 'Payment settled to the wallet',
    dispute: '{{1}} dispute {{2}} ({{3}})',
    nameChangeClosed: 'Name-change request closed: {{1}}',
    nameChangeEnded: 'Name-change request {{1}}',
    panelOperation: 'VNNIC panel operation {{1}}: {{2}}',
    addonCancelled: 'Add-on cancelled: {{1}}',
    registryRefusedLock: 'The registry refused the lock: {{1}}',
    passwordNext: 'Password accepted; next: {{1}}',
    secondFactor: 'Second factor: {{1}}',
    stepUp: 'Re-authenticated: {{1}}',
    dossierDecision: '{{1}}: {{2}} (verification method: {{3}})',
    staffReview: 'Correction asked in a staff review: {{2}} (work item {{1}})',
    nameChangeDossier: '{{1}} (name-change request {{2}})',
    domainReturn: 'Return of the domain (declaration {{1}})',
    proofSubmission: '{{1}}: {{3}} {{4}} (submission {{2}}, file {{5}})',
    proofAccepted: 'Proof of identity accepted (verification {{1}})',
    fileRecovered: 'File {{1}} submitted to its review path automatically: uploaded {{2}} (by: {{3}}) with no check, decision or submission (rule of 2026-10-06: uploading is submitting)',
    registryContact: 'For registry contact {{1}}',
    sessionsRevoked: '{{1}} session(s) revoked',
    domainState: 'The domain is {{1}}',
    cannotRenew: 'The domain is {{1}}, so it cannot be renewed (restore it if it is in redemption)',
    registryCost: 'Registry cost of {{1}}',
    auction: 'Auction of {{1}}',
    backorderCatch: 'Backorder caught: {{1}}',
    bankTransfer: 'Bank transfer, payment code {{1}}',
    refundToPayer: 'Refund to the payment source: {{1}}',
    refundToPayerFailed: 'Refund to the payment source failed, money back in the wallet',
    withReference: '{{1}} (reference {{2}})',
    // a hold capture: the order operation (commerce:enum.operation) and the domain name
    capture: '{{1}}: {{2}}',
    value: {
      rejected: 'rejected',
      cancelled: 'cancelled',
      REFUSED: 'refused',
      CANCELLED: 'cancelled',
      MFA: 'two-factor code',
      ENROL: 'two-factor enrolment',
      ACTIVE: 'signed in',
      otp: 'authenticator code (TOTP)',
      rcv: 'recovery code',
      MANUAL: 'Staff review',
      AUTOMATIC: 'Automatic',
      reviewer_approved_all_requirements_met: 'approved by staff, all requirements met',
      all_policy_requirements_met: 'all policy requirements met',
      PROOF_SUBMISSION: 'Proof of identity submitted',
      registration: 'registration declaration',
      'subject declaration': 'subject declaration',
    },
    original: 'Original text: {{text}}',
  },
  // A registry command's result by its EPP result code (RFC 5730 §3; lib/eppResult.ts).
  eppResult: {
    success: 'The registry carried out the command.',
    failure: 'The registry refused the command.',
    1000: 'The registry carried out the command.',
    1001: 'The registry accepted the command; the action is pending.',
    1300: 'No messages in the queue.',
    1301: 'Message received; acknowledge it to get the next one.',
    1500: 'The session with the registry ended.',
    2000: 'The registry does not know the command.',
    2001: 'Command syntax error.',
    2002: 'The command cannot be used here.',
    2003: 'A required value is missing.',
    2004: 'A value is out of range.',
    2005: 'A value has the wrong format.',
    2100: 'The registry does not support this protocol version.',
    2101: 'The registry does not support this command.',
    2102: 'The registry does not support this option.',
    2103: 'The registry does not support this extension.',
    2104: 'Refused for a billing failure (the balance at the registry).',
    2105: 'The object is not eligible for renewal.',
    2106: 'The object is not eligible for transfer.',
    2200: 'The registry refused the authentication.',
    2201: 'The registrar is not authorised for this object.',
    2202: 'Wrong authorisation code (auth info).',
    2300: 'The object has a pending transfer.',
    2301: 'The object has no pending transfer.',
    2302: 'The object already exists at the registry.',
    2303: 'The object does not exist at the registry.',
    2304: 'The object status does not allow this.',
    2305: 'The object is in use elsewhere, so this is not allowed.',
    2306: "A value breaks the registry's policy.",
    2307: 'The registry does not offer this service for the object.',
    2308: "The registry's data management policy does not allow this.",
    2400: 'The registry failed to process the command; the outcome is uncertain.',
    2500: 'The registry failed and closed the connection.',
    2501: 'The registry refused the authentication and closed the connection.',
    2502: 'Session limit exceeded; the registry closed the connection.',
  },
  apiErrors: {
    detail: 'Technical detail (include it when reporting)',
    codes: {
      ACCOUNT_LOCKED: 'The account is locked.',
      ACCOUNT_NOT_OPEN: 'The customer\'s wallet is frozen or closed, so nothing can be posted to it.',
      ADDONS_UNAVAILABLE: 'The add-ons cannot be reached right now. Try again in a few minutes.',
      ADDON_BUSY: 'The add-on is busy with too many things at once. Try again in a few minutes.',
      ADDON_FAILED: 'The add-on failed while handling this. Try again; if it fails again, tell whoever looks after the add-on.',
      ADDON_NOT_RUNNING: 'The add-on is not running. Switch it on in Cài đặt › Module › Tiện ích.',
      ADDON_TIMEOUT: 'The add-on took too long to answer and was stopped. Try again in a few minutes.',
      ANSWER_TOO_LARGE: 'The add-on\'s answer is too large to show. Narrow the filter and try again.',
      NOT_PARKED: 'This event is no longer held back (it was sent again). Reload the page to see where it stands.',
      NOT_PRODUCTION_READY: 'This part is for development and tests only and cannot be used on the production server.',
      TOO_LARGE: 'The data sent to the add-on is too large.',
      TRY_AGAIN: 'The add-on cannot do this right now. Try again in a few minutes.',
      UNKNOWN_ADDON: 'There is no such add-on, or it is not installed on this server.',
      ADJUSTED_INVOICE: 'This invoice was adjusted, so it is no longer replaced or cancelled; adjust it again.',
      ADJUSTMENT_NOT_CORRECTED: 'An adjusting invoice is not corrected itself; adjust the original again.',
      ALREADY_CLOSED: 'This was already closed.',
      ALREADY_DISABLED: 'This was already turned off.',
      ALREADY_LINKED: 'This is already linked.',
      APPROVAL_EXPIRED: 'The approval request expired. Send a new one.',
      APPROVAL_PENDING: 'The request is waiting for a second person.',
      APPROVAL_REQUIRED: 'This needs an approval first.',
      APPROVAL_SELF: 'The person who asked cannot approve their own request.',
      APPROVAL_STALE: 'The customer\'s invoice details changed after the approval. Send a new request.',
      APPROVAL_USED: 'This request was carried out already.',
      AWAITING_CONFIRMATION: 'The domain is protected: the request is recorded and waits for confirmation by authorised staff.',
      CANCEL_NOT_ALLOWED: 'The e-invoice settings do not allow cancelling; adjust or replace instead.',
      CHANGE_IN_PROGRESS: 'Another change is still in progress. Wait for it to finish and try again.',
      CHANGED_SINCE_PROPOSED: 'The place changed after this was proposed. Propose it again.',
      CHANNEL_REQUIRED: 'Choose a registration channel.',
      CHECKS_FAILED: 'The checks before running did not pass.',
      CODE_TAKEN: 'This code is already in use.',
      CONCURRENT_MODIFICATION: 'Someone else just changed this. Reload the page and try again.',
      CONFLICT: 'This conflicts with another change. Reload the page and try again.',
      CONFIRMATIONS_REQUIRED: 'Approve this from Settings › Connections › Reporting API, where the three points about the receiving system are confirmed.',
      CONTACTS_RULE: 'This now sends customer e-mail and phone: propose it again so that someone allowed to handle personal data approves it.',
      CREDIT_NOTE_NO_EINVOICE: 'A credit note has no e-invoice of its own: adjust its invoice\'s e-invoice down.',
      CREDIT_NOTE_USED: 'This credit note is on an adjusting invoice already.',
      DOMAIN_NOT_REGISTERED_HERE: 'This product takes only a domain registered with Tino: register or transfer the domain first, then create the service.',
      DOMAIN_OWN_NOT_ALLOWED: 'This product takes no domain the customer already has, so staff cannot create its service yet (there is no cart to register or transfer a domain). Turn on “Allow a domain the customer has” in the Domains tab of the product.',
      EINVOICE_PROVIDER_NOT_ANSWERING: 'The e-invoice provider did not answer. Try again later.',
      EINVOICE_PROVIDER_REFUSED: 'The e-invoice provider refused the request.',
      EINVOICE_PROVIDER_UNAVAILABLE: 'No e-invoice provider is switched on (Settings › Modules › E-invoice).',
      EINVOICE_SETTINGS_UNAVAILABLE: 'The e-invoice settings in force cannot be read.',
      INCREASE_NOT_SUPPORTED: 'Adjusting up is not supported: amounts are corrected with credit notes.',
      IN_PROGRESS: 'The provider is being called for this invoice. Wait for the outcome and reload.',
      MAPPING_INVALID: 'The Zalo template setup is not valid. Fix the marked parameters and save again.',
      NOT_ACTIVE: 'The key is no longer in use.',
      CONNECTION_RETIRED: 'This connection is retired.',
      CUSTOMER_NOT_ACTIVE: 'The customer account is not active.',
      DECISION_REQUIRED: 'Choose a decision.',
      DESTINATION_NOT_ALLOWED: 'The address must start with https and be reachable from the Internet. A system that runs only inside a private network cannot receive signals: let it fetch on its schedule.',
      DECLARATIONS_DISABLED: 'Declarations are turned off.',
      DOCUMENTS_MISSING: 'The dossier lacks documents.',
      DOMAIN_CHANGED: 'The domain changed while the registry was being read, so nothing was recorded. Try again in a moment.',
      DOMAIN_PROTECTED: 'The domain is protected, so this cannot be done online.',
      DUPLICATE_REFERENCE: 'This transaction code was recorded before. Check it so the money is not counted twice.',
      EXPORT_DISABLED: 'Export is turned off.',
      FORBIDDEN: 'You are not allowed to do this.',
      FOUR_EYES_APPROVAL_REQUIRED: 'Another staff member must approve this first.',
      GATEWAY_UNAVAILABLE: 'The external service cannot be reached right now. Try again later.',
      IDEMPOTENCY_KEY_REQUIRED: 'The request lacks its duplicate-protection key. Reload the page and try again.',
      IDEMPOTENCY_KEY_REUSED: 'The form changed after a failed send; send again to make a new request.',
      INDIVIDUAL_EKYC_ONLY: 'An individual is verified by eKYC only.',
      INSUFFICIENT_FUNDS: 'The customer\'s balance is not enough for this operation.',
      INTERNAL_ERROR: 'The server failed to process the request. Try again; if it persists, report it with the request ID.',
      INVALID: 'Some input is not valid. Check the fields and send again.',
      INVALID_CREDENTIALS: 'Wrong e-mail or password.',
      INVALID_CURSOR: 'This page of results is out of date. Reload the list.',
      INVALID_DATE: 'The date is not valid.',
      KEYS_WITHOUT_ADDRESSES: 'Some keys have no address list. Before customer e-mail and phone are sent, rotate those keys (giving their addresses) or revoke them.',
      INVALID_DOMAIN: 'The domain name is not valid.',
      INVALID_QUERY: 'The filter is not valid. Clear it and try again.',
      NOT_CANCELLABLE_NOW: 'This cancellation cannot be sent in its current state.',
      NOT_ISSUABLE_NOW: 'An e-invoice in this state is not issued (again).',
      NOT_ISSUED: 'The e-invoice is not issued.',
      NOT_RECONCILING: 'This e-invoice has no key to look up.',
      NOT_WITHDRAWABLE: 'Only an adjusting or replacing invoice never issued can be dropped.',
      ORIGINAL_NOT_WITHDRAWN: 'An invoice\'s own e-invoice is not dropped; cancel or replace it if needed.',
      PARAMS_INCOMPLETE: "The sample data does not fill every parameter of the Zalo template.",
      PHONE_INVALID: 'A Vietnamese mobile number is required.',
      PROVIDER_CANNOT: 'The e-invoice provider switched on cannot do this.',
      PROVIDER_NOT_READY: 'The connection is not ready (no access code yet). See its status.',
      PROVIDER_REFUSED: 'The provider refused the request (its code is in the details).',
      PROVIDER_UNREACHABLE: 'The provider could not be reached. Try again later.',
      MODULE_UNAVAILABLE: 'The connection cannot be used yet: switch it on or set it up in Settings › Modules.',
      MISSING_AT_PROVIDER: 'The hosting server no longer holds this account: the mismatch is recorded. Check it on WHM before acting on the service.',
      USERNAME_TAKEN: 'Another account on the hosting server has this username.',
      DOMAIN_TAKEN: 'Another account on the hosting server has this domain.',
      SAME_FORM_REQUIRED: 'The invoice was corrected one way (adjusting or replacing); later corrections use the same way.',
      UNEXPECTED_RESPONSE: 'The server answered in an unexpected form. Try again in a few minutes.',
      INVALID_STATE: 'This cannot be done in the current state. Reload the page to see the latest state.',
      INVALID_STATUS: 'This cannot be done in the current state. Reload the page to see the latest state.',
      MANDATORY_ADDON: 'This add-on is mandatory and cannot be turned off.',
      METHOD_NOT_ALLOWED: 'This function on the server in use does not accept this kind of request. Update the system and try again.',
      MFA_REQUIRED: 'This action needs a session signed in with an authentication code.',
      NETWORK_ERROR: 'The server could not be reached. Check the network and try again.',
      NOTHING_TO_SIGN: 'There is no file to sign.',
      NOT_CONFIGURED: 'The connection this action needs is not configured.',
      NOT_FOUND: 'The requested data was not found. It may have been removed, or you may not be allowed to see it.',
      NOT_OFFERED: 'This TLD is not on sale.',
      NO_CHANGE: 'Nothing differs from what is set now.',
      NO_PRICE: 'There is no selling price for this operation. Set the TLD price first.',
      NO_TAX_CODE: 'Neither the plan nor its group has a VAT rate: set one on the group or on the plan first.',
      HOURLY_NOT_BUILT: 'An hourly plan can be quoted, not ordered yet.',
      LOCATION_NOT_OFFERED: 'This plan does not offer the chosen location to customers. Choose another location or let the system choose.',
      OUT_OF_STOCK: 'The plan is out of stock at this location (closed, sold out or no stock set). Choose another location or add stock to the plan.',
      OPTION_REQUIRED: 'A required option of the plan is missing. Choose every required option and send again.',
      OPTION_UNKNOWN: 'An option does not belong to this plan or can no longer be ordered. Reload the page and choose again.',
      OPTION_NOT_ALLOWED: 'An option cannot be chosen together with the other choices, or the text entered is too long.',
      OPTION_QUANTITY: 'An option quantity is outside its limits or off the plan\'s step.',
      HOURLY_PRICE_INVALID: 'The hourly price is not valid: an hourly plan price has no setup fee; an hourly option price is a percent only (0 to 999.9999, at most 4 decimals); other cycles take no percent.',
      FULFILMENT_INVALID: 'The module connection of the plan is not right: the “Ready to sell” table of its Module connection tab shows which condition fails.',
      FULFILMENT_CHANGED: 'The server group or the module connection changed while it was checked. Press Check, then save again.',
      GROUP_MODULE_MISMATCH: 'The server group uses another module: pick a group of the right module, or connections of the right module for the group.',
      NOT_OVERRIDABLE: 'A plan cannot override this setting: it always comes from the server group.',
      SETTINGS_INVALID: 'The placement settings are not valid for the module: the technical details name the field.',
      MEMBER_NOT_FOUND: 'The group’s module has no such connection: pick an existing one under Settings › Modules.',
      MIXED_MEMBERS: 'A connection joins a group one way only: as a whole, or by its servers.',
      MAPS_TO_UNKNOWN: 'The setting that takes the choice is not one of this kind of service or of a module its plans use.',
      OPTION_MODULE_MISMATCH: 'This option type belongs to a module none of the product’s plans uses.',
      SALE_MODE_MISMATCH: 'This does not fit how the group is sold: a plan of an hourly group offers 1 hour only, a plan of a cycle group never does, and a product with plans moves only to a group sold the same way.',
      OPERATION_BLOCKED: 'The operation is blocked (a lock, a protection or the domain state does not allow it).',
      PAYLOAD_TOO_LARGE: 'The data sent is too large. Shorten it or choose a smaller file, then send again.',
      PENDING_CONFIRMATION: 'Sent; the registry has not confirmed the outcome yet. Reconciliation will update it.',
      PRICE_CONFLICT: 'Another price applies over the same period.',
      QUOTE_UNUSABLE: 'The quote expired or can no longer be used. Get a new quote.',
      RATE_LIMITED: 'Too many attempts. Wait a moment and try again.',
      REASON_REQUIRED: 'A reason is required for this action.',
      REGISTRIES_UNAVAILABLE: 'The registry cannot be reached right now.',
      REGISTRY_NOT_SENT: "The command was not sent to the registry: the connection stopped it before sending (the connection's reason is in the technical detail).",
      REGISTRY_REJECTED: 'The registry refused the request.',
      REGISTRY_SYNC_COOLDOWN: 'The domain was synced with the registry moments ago. Wait about 30 seconds and try again.',
      REGISTRY_SYNC_IN_PROGRESS: 'This domain is being read at the registry. Wait for that read to finish, then reload the page.',
      REGISTRY_UNAVAILABLE: 'The registry cannot be reached right now.',
      REGISTRY_UNKNOWN: 'The registry did not answer (outcome unknown), so whether it matches is not known; nothing was changed. Try again in a few minutes.',
      ALREADY_IN_SYSTEM: 'This name is already in the platform.',
      NOT_AT_REGISTRY: 'The registry does not hold this name.',
      NOT_SPONSORED: 'The registry holds this name for another registrar, not Tino.',
      REGISTRY_STATE_NOT_IMPORTABLE: 'The state at the registry does not allow bringing the name in (expired, pending delete, pending transfer or not created yet).',
      TLD_NOT_CONFIGURED: 'This TLD is not set up in the platform.',
      REGISTRANT_NOT_MATCHED: 'No subject matches the identity at the registry: choose the subject.',
      REGISTRANT_AMBIGUOUS: 'Several subjects hold the identity number at the registry: choose the subject.',
      SUBJECT_NOT_FOUND: 'The subject was not found.',
      SUBJECT_NOT_ACTIVE: 'The subject is no longer active.',
      SUBJECT_OF_ANOTHER_CUSTOMER: 'This subject is managed by another customer.',
      IMPORT_ALL_RUNNING: 'A bulk import of this connection is running. Wait for it to finish.',
      NOTHING_TO_IMPORT: 'The provider lists no name the platform does not have.',
      REQUEST_IN_PROGRESS: 'An identical request is still being processed. Wait a moment and reload.',
      REVEAL_DENIED: 'You may not see this in full.',
      ROUTE_NOT_FOUND: 'This function is not available on the server in use. Update the system and try again.',
      SELF_APPROVAL: 'You cannot approve your own request.',
      SEND_FAILED: 'Sending failed. Try again later.',
      SIGNING_OFF: 'The signing service is turned off.',
      SOURCE_TIMEOUT: 'The data source answered too slowly. Try again later.',
      SOURCE_UNAVAILABLE: 'The data source cannot be reached right now. Try again later.',
      STAFF_VERIFICATION_REMOVED: 'Staff can no longer verify subjects themselves: use eKYC or digitally signed documents.',
      SYNC_RUNNING: 'Another sync is running. Wait for it to finish and try again.',
      TEST_RATE_LIMITED: 'A test just ran. Wait a moment before testing again.',
      TLD_NOT_SUPPORTED: 'This TLD is not supported.',
      TOTP_INVALID: 'The authenticator code is wrong or was already used. Wait for the next code and try again.',
      TOTP_REQUIRED: 'An authentication code (TOTP) is required.',
      UNAUTHENTICATED: 'Your session has ended. Sign in again to continue.',
      UNSUPPORTED_MEDIA_TYPE: 'The format of the data sent is not supported. Reload the page and try again.',
      VALIDATION_FAILED: 'Some input is not valid. Check the fields and send again.',
      VERSION_CONFLICT: 'Someone else just changed this. Reload the page and try again.',
      VNNIC_FILE_NOT_FOUND: 'VNNIC has no such file.',
      VNNIC_FILE_UNAVAILABLE: 'The file cannot be fetched from VNNIC right now. Try again later.',
      WAITS_FOR_RUN: 'The daily 17:00 run creates and signs this e-invoice; nothing is sent outside it. Only a failed e-invoice can be sent again.',
      WEAK_PASSWORD: 'The password is too weak.',
      WRITES_DISABLED: 'This server is not connected to the registries, so the action cannot be done here.',
      ZONE_FILE_INVALID: 'The zone file cannot be read: check the line it names.',
      ZONE_RECORD_DATA_INVALID: 'A record value is not written the way its type needs.',
      ZONE_RECORDS_INVALID: 'The records break a rule of the DNS zone.',
      ZONE_REFUSED: 'The DNS zone cannot be changed now (it is not active, or another change is being sent).',
      ZONE_TEMPLATE_INVALID: 'The template cannot be applied: check the values it asks for.',
    },
    status: {
      network: 'The server could not be reached. Check the network and try again.',
      unauthenticated: 'Your session has ended. Sign in again to continue.',
      forbidden: 'You are not allowed to do this.',
      notFound: 'The requested data was not found.',
      conflict: 'This cannot be done in the current state. Reload the page to see the latest state.',
      rateLimited: 'Too many attempts. Wait a moment and try again.',
      server: 'The server or a service it uses failed. Try again later; if it persists, report it with the request ID.',
      invalid: 'The request is not valid. Check the input and send again.',
      other: 'The request failed.',
    },
  },
  write: {
    failed: 'The request failed.',
    meta: '{{code}} · request {{requestId}}',
    mfaRequired: 'This action needs a session signed in with your authenticator code.',
    mfaLink: 'Sign in again with TOTP',
    fourEyes: 'A second operator must approve this first (four-eyes). Request the approval, have it approved, then retry with its ID.',
    selfApproval: 'You requested this approval; a different operator must decide it.',
    forbidden: 'Your roles do not include the permission this action needs.',
    keyReused: 'The form changed after a failed attempt; submit again to send it as a new request.',
    inProgress: 'The same request is still being processed. Wait a moment, then reload.',
    registryUnavailable: 'The registry could not be reached. Nothing was re-sent; check the registry transaction before retrying.',
    domainProtected: 'The domain is protected: a name change cannot be requested online. Contact the registrar\'s staff, who handle it manually.',
    registrantConsentRequired: 'Consent is not recorded here: each registrant agrees with the link in the e-mail they received.',
    notifyCustomer: 'Send the webhook to the customer',
    notifyCustomerHint: 'Untick to keep this action\'s webhooks from the owning customer\'s systems. Platform (CRM) webhooks are still sent.',
  },
  reason: {
    title: 'A reason is required',
    intro: 'This query names a domain, person or identifier. State why you need it (for example an incident or ticket reference). The reason is recorded with the query in the audit log.',
    barIntro: 'Queries naming business objects are audited with this reason.',
    label: 'Reason (at least {{min}} characters)',
    placeholder: 'e.g. INC-2291: renewal stuck for example.net',
    sentAs: 'Sent as {{value}} (headers carry plain ASCII only)',
    submit: 'Save reason and run',
    current: 'Query reason:',
    change: 'Change',
    clear: 'Clear',
    none: 'No query reason set. Queries naming domains, people or IDs will ask for one.',
    set: 'Set reason',
    notConfigured: 'Not available in this environment',
    notConfiguredBody: '{{what}} is not set up for this environment. The rest of the console keeps working; ask the platform team to set it up.',
    /** What is missing, by the kind of data source that is not set up. */
    sources: {
      metrics: 'Metrics',
      logs: 'Log search',
      traces: 'Trace search',
      alerts: 'Alerting',
      slo: 'Service level objectives',
      other: 'This data source',
    },
  },
  reveal: {
    hidden: '{{label}}, hidden',
    sensitive: 'Highly sensitive',
    sensitiveHint: 'Highly sensitive. Revealing it is recorded in the audit log.',
    reveal: 'Reveal',
    hide: 'Hide',
    revealedNote: 'Access recorded in the audit log. Hidden again automatically.',
    dialogTitle: 'Reveal identity number',
    dialogBody: 'Revealing this {{label}} is recorded in the audit log with your identity and reason. It is shown for a short time only and never stored in the browser.',
    reasonLabel: 'Reason',
    ticket: 'Ticket or case reference (optional)',
    ticketPlaceholder: 'e.g. SUP-1042',
    confirm: 'Reveal and log access',
    revealing: 'Revealing…',
    reasons: {
      support: 'Customer support request',
      vnnic: 'VNNIC / compliance verification',
      legal: 'Legal or law-enforcement request',
      abuse: 'Abuse or fraud investigation',
    },
  },
  /**
   * Readable labels for backend codes (src/lib/labels.ts). Keys are the code in UPPER_SNAKE:
   * `DomainRenewalWorkflow` -> DOMAIN_RENEWAL, `domain.nameservers.update` -> DOMAIN_NAMESERVERS_UPDATE.
   * Unknown codes fall back to the code turned into words, never the raw constant.
   */
  labels: {
    policyVersion: '{{name}} · version {{version}}',
    /** Compliance policy keys (policies/**, policy snapshots). */
    policy: {
      DOSSIER_REQUIREMENTS: 'Dossier requirements',
      VN_IDENTITY_VERIFICATION: 'Identity verification (.vn)',
      VN_REGISTRATION_ELIGIBILITY: '.vn registration eligibility',
      GTLD_REGISTRATION_ELIGIBILITY: 'gTLD registration eligibility',
      VN_EKYC_VNNIC: 'VNNIC eKYC',
      VN_HSTM_CATALOG_MAPPING: 'VNNIC dossier (HSTM) catalogue mapping',
      VN_SUBJECT_VERIFICATION: 'Registrant verification (.vn)',
      VN_ID_VN_REGISTRATION: '.id.vn registration',
      VN_EXPIRATION: '.vn expiry rules',
      VN_REGISTRATION_DATA_PUBLICATION: '.vn registration data publication',
      REGISTRAR_ALL_DOMAINS_DOSSIER: 'Dossier required for every domain',
      REGISTRAR_ALL_DOMAINS_VERIFICATION: 'Registrant verification for every domain',
      REGISTRAR_ABUSE_HANDLING: 'Abuse handling',
      REGISTRAR_AUCTION_RULES: 'Auction rules',
      REGISTRAR_BACKORDER_RULES: 'Backorder rules',
      REGISTRAR_DATA_RETENTION: 'Data retention',
      REGISTRAR_LOCK_EFFECTS: 'Lock effects',
      EXPIRY_NOTICES: 'Expiry notices',
      ICANN_GTLD_EXPIRATION: 'gTLD expiry and renewal (ICANN)',
      ICANN_REGISTRAR_DATA_ESCROW: 'Registrar data escrow (ICANN)',
      ICANN_REGISTRATION_DATA_DISCLOSURE: 'Registration data disclosure (ICANN)',
      ICANN_REGISTRATION_DATA_PUBLICATION: 'Registration data publication (ICANN)',
      ICANN_TRANSFER_POLICY: 'Transfer policy (ICANN)',
      ICANN_UDRP: 'UDRP disputes (ICANN)',
      ICANN_WHOIS_ACCURACY: 'WHOIS accuracy (ICANN)',
    },
    /** Workflow types, in either spelling (DOMAIN_RENEWAL or DomainRenewalWorkflow). */
    workflow: {
      DOMAIN_REGISTRATION: 'Domain registration',
      DOMAIN_RENEWAL: 'Domain renewal',
      DOMAIN_UPDATE: 'Domain update',
      DOMAIN_EXPIRY: 'Expiry handling',
      DOMAIN_RESTORE: 'Domain restore',
      DOMAIN_DELETE: 'Domain deletion',
      DOMAIN_TRANSFER_IN: 'Transfer in',
      DOMAIN_TRANSFER_OUT: 'Transfer out',
      DOMAIN_REGISTRANT_CHANGE: 'Registrant change',
      VNNIC_SYNC: 'VNNIC dossier sync',
      REGISTRY_POLL: 'Registry notices',
    },
    /** Workflow steps (current step of a running workflow). */
    workflowStep: {
      AUTHORIZE: 'Checking authorisation',
      DOSSIER: 'Checking the dossier',
      GATE: 'Checking eligibility',
      PREPARE: 'Preparing',
      CONTACT: 'Preparing registry contacts',
      REGISTRY_CREATE: 'Creating at the registry',
      REGISTRY_RENEW: 'Renewing at the registry',
      REGISTRY_RESTORE: 'Restoring at the registry',
      REGISTRY_UPDATE: 'Updating at the registry',
      REGISTRY_REQUEST: 'Sending the request to the registry',
      AWAIT_REGISTRY: 'Waiting for the registry',
      UPDATE_REGISTRANT: 'Updating the registrant',
      CONFIRM_CURRENT_REGISTRANT: 'Waiting for the current registrant to confirm',
      CONFIRM_NEW_REGISTRANT: 'Waiting for the new registrant to confirm',
      SUBJECT_VERIFY: 'Verifying the registrant',
      EPP_CREATE: 'Creating at the registry',
      EPP_CREATE_RECONCILE: 'Checking the registry for the create result',
      EPP_RENEW: 'Renewing at the registry',
      EPP_TRANSFER: 'Transferring at the registry',
      EPP_UPDATE: 'Updating at the registry',
      ACTIVATE: 'Activating',
      FINALIZE: 'Finishing',
      COMPENSATE: 'Undoing after a failure',
      ROLLBACK: 'Rolling back',
      ABANDON: 'Abandoning',
      UNHOLD: 'Releasing held funds',
      LOCK_TRANSFER: 'Locking the transfer',
      NEW_AUTH_CODE: 'Changing the Auth Code',
    },
    /** Domain history and platform event types. */
    event: {
      DOMAIN_REGISTRATION_REQUESTED: 'Registration requested',
      DOMAIN_TRANSFER_IN_REQUESTED: 'Transfer in requested',
      DOMAIN_CREATED_AT_REGISTRY: 'Created at the registry',
      DOMAIN_REGISTERED: 'Domain registered',
      DOMAIN_ACTIVATED: 'Domain activated',
      DOMAIN_RENEWAL_REQUESTED: 'Renewal requested',
      DOMAIN_RENEWED: 'Domain renewed',
      DOMAIN_RENEWAL_OVERRIDE: 'Renewed again by staff within 24 hours of the last renewal',
      DOMAIN_REGISTRY_AUTO_RENEW_OBSERVED: 'Registry auto-renewal detected',
      DOMAIN_REGISTRY_EXPIRY_OBSERVED: 'Registry expiry date detected',
      DOMAIN_REGISTRY_STATUSES_OBSERVED: 'Registry statuses detected',
      DOMAIN_REGISTRY_OUTCOME_UNKNOWN: 'Registry result unknown',
      DOMAIN_LIFECYCLE_CHANGED: 'Lifecycle state changed',
      DOMAIN_EXPIRED: 'Domain expired',
      DOMAIN_GRACE_PERIOD_ENTERED: 'Grace period started',
      DOMAIN_REDEMPTION_ENTERED: 'Redemption period started',
      DOMAIN_EXPIRY_NOTICE: 'Expiry notice sent',
      DOMAIN_DELETE_REQUESTED: 'Deletion requested',
      DOMAIN_DELETED: 'Domain deleted',
      DOMAIN_RESTORED: 'Domain restored',
      DOMAIN_NAMESERVERS_CHANGE_REQUESTED: 'Nameserver change requested',
      DOMAIN_NAMESERVERS_CHANGED: 'Nameservers changed',
      DOMAIN_DNSSEC_CHANGED: 'DNSSEC changed',
      DOMAIN_AUTHINFO_CHANGED: 'AuthInfo code changed',
      DOMAIN_REGISTRANT_CHANGE_REQUESTED: 'Registrant change requested',
      DOMAIN_REGISTRANT_CHANGE_CANCELLED: 'Registrant change cancelled',
      DOMAIN_REGISTRANT_CHANGE_REJECTED: 'Registrant change rejected',
      DOMAIN_REGISTRANT_CHANGED: 'Registrant changed',
      DOMAIN_CONTACTS_CHANGED: 'Contacts updated',
      DOMAIN_TRANSFER_OUT_REQUESTED: 'Transfer out requested',
      DOMAIN_TRANSFER_OUT_REJECTED: 'Transfer out rejected',
      DOMAIN_TRANSFER_OUT_CANCELLED: 'Transfer out cancelled',
      DOMAIN_TRANSFERRED_OUT: 'Transferred out',
      DOMAIN_TRANSFERRED_IN: 'Transferred in',
      DOMAIN_SUSPENDED: 'Domain suspended',
      DOMAIN_UNSUSPENDED: 'Suspension lifted',
      DOMAIN_LOCK_APPLIED: 'Lock applied',
      DOMAIN_LOCK_RELEASED: 'Lock released',
      DOMAIN_DISPUTE_OPENED: 'Dispute opened',
      DOMAIN_DISPUTE_DECIDED: 'Dispute decided',
      DOMAIN_DISPUTE_IMPLEMENTED: 'Dispute decision carried out',
      DOMAIN_DISPUTE_CLOSED: 'Dispute closed',
      DOMAIN_VNNIC_DOSSIER_ACKNOWLEDGED: 'VNNIC accepted the dossier',
      DOMAIN_VNNIC_DOSSIER_REJECTED: 'VNNIC rejected the dossier',
      DOMAIN_VNNIC_DOSSIER_CORRECTION_REQUIRED: 'VNNIC asked for a dossier correction',
      VNNIC_SYNC_SUBMITTED: 'Dossier sent to VNNIC',
      VNNIC_SYNC_ACKNOWLEDGED: 'VNNIC accepted the dossier',
      VNNIC_SYNC_REJECTED: 'VNNIC rejected the dossier',
      VNNIC_SYNC_CORRECTION_REQUIRED: 'VNNIC asked for a dossier correction',
      VNNIC_REPORT_GENERATED: 'VNNIC report generated',
      ABUSE_DOMAIN_ACTION_REQUESTED: 'Abuse action requested',
      DOSSIER_OPENED: 'Dossier opened',
      DOSSIER_SUBJECT_ATTACHED: 'Registrant added to the dossier',
      DOSSIER_DOCUMENT_BOUND: 'Document added to the dossier',
      DOSSIER_BOUND: 'Dossier linked',
      DOSSIER_CONSENT_RECORDED: 'Consent recorded',
      DOSSIER_COMPLETED: 'Dossier completed',
      DOSSIER_STATUS_CHANGED: 'Dossier status changed',
      DOSSIER_INVALIDATED: 'Dossier invalidated',
      DOSSIER_SUPERSEDED: 'Dossier replaced by a newer version',
      REGISTRY_POLL_MESSAGE: 'Registry notice received',
      DOMAIN_EMAIL_SENT: 'E-mail sent by staff',
      REGISTRY_POLL_MESSAGE_RECEIVED: 'Registry notice received',
      CUSTOMER_CREATED: 'Customer created',
      CUSTOMER_STATUS_CHANGED: 'Customer status changed',
      RESELLER_CREATED: 'Reseller created',
      SUBJECT_CREATED: 'Registrant created',
      SUBJECT_VERSION_CREATED: 'New registrant version',
      SUBJECT_VERIFICATION_STARTED: 'Verification started',
      SUBJECT_VERIFICATION_COMPLETED: 'Verification completed',
      SUBJECT_VERIFICATION_REVOKED: 'Verification revoked',
      SUBJECT_VERIFICATION_EXPIRED: 'Verification expired',
      ORDER_PLACED: 'Order placed',
      ORDER_ITEM_FULFILLED: 'Order item completed',
      ORDER_ITEM_FAILED: 'Order item failed',
      PAYMENT_SUCCEEDED: 'Payment received',
      INVOICE_ISSUED: 'Invoice issued',
      CREDIT_NOTE_ISSUED: 'Credit note issued',
      DROP_SCHEDULED: 'Release date scheduled',
      BACKORDER_PLACED: 'Backorder placed',
      BACKORDER_CANCELLED: 'Backorder cancelled',
      BACKORDER_ASSIGNED: 'Backorder assigned',
      BACKORDER_RETURNED: 'Backorder returned',
      BACKORDER_CAUGHT: 'Domain caught',
      BACKORDER_NOT_CAUGHT: 'Domain not caught',
      BACKORDER_WON: 'Backorder won',
      BACKORDER_LOST: 'Backorder lost',
      AUCTION_CREATED: 'Auction created',
      AUCTION_OPENED: 'Auction opened',
      AUCTION_EXTENDED: 'Auction extended',
      AUCTION_CLOSED: 'Auction closed',
      AUCTION_ENDED: 'Auction ended',
      AUCTION_CANCELLED: 'Auction cancelled',
      AUCTION_UNSOLD: 'Auction ended without a winner',
      BID_ACCEPTED: 'Bid accepted',
      BID_REJECTED: 'Bid rejected',
      WINNER_SELECTED: 'Winner selected',
      SETTLEMENT_STARTED: 'Settlement started',
      SETTLEMENT_FAILED: 'Settlement failed',
      REGISTRATION_RESUMED: 'Registration resumed',
      REGISTRATION_CANCELLED: 'Registration cancelled by staff',
      REGISTRATION_REACTIVATED: 'Registration order reactivated by staff',
      REGISTRATION_STOPPED: 'Registration stopped for staff',
      REGISTRY_COMMAND_REFUSED: 'Registry refused a command',
      DOMAIN_TRANSFER_IN_CANCELLED: 'Transfer in cancelled',
      DOMAIN_DELETED_BY_REGISTRY: 'Deleted by the registry',
      DOMAIN_IMPORTED: 'Brought in from the registry',
      DOMAIN_PARKED: 'Name servers parked (expiry)',
      DOMAIN_UNPARKED: 'Name servers restored after renewal',
      DOMAIN_PENDING_ACTION_RESULT: 'Registry reported a pending action result',
      DOMAIN_DISPUTE_REGISTRY_LOCKED: 'Locked at the registry for a dispute',
      DOMAIN_DISPUTE_REGISTRY_UNLOCKED: 'Unlocked at the registry after a dispute',
      DOSSIER_FORM_UPDATED: 'Dossier registration form updated',
      DOSSIER_REGISTRAR_ATTESTED: 'Registrar confirmed the declaration',
      DOSSIER_FILE_REGISTRAR_SIGNED: 'Registrar signed a dossier file',
    },
    /** Audit log actions (dotted codes such as domain.nameservers.update). */
    audit: {
      SUPPORT_ARTICLE_SAVE: 'Save knowledge article',
      SUPPORT_ATTACHMENT_READ: 'Read support attachment',
      SUPPORT_ATTACHMENT_UPLOAD: 'Upload support attachment',
      SUPPORT_CATEGORY_SAVE: 'Save knowledge category',
      SUPPORT_DEPARTMENT_SAVE: 'Save support department',
      SUPPORT_INBOUND_RECEIVE: 'Receive support message',
      SUPPORT_MESSAGE_ADD: 'Add support message',
      SUPPORT_PARTICIPANT_ADD: 'Add support participant',
      SUPPORT_SLA_SIGNAL: 'Record support deadline signal',
      SUPPORT_TICKET_ASSIGN: 'Assign support ticket',
      SUPPORT_TICKET_OPEN: 'Open support ticket',
      SUPPORT_TICKET_TRANSITION: 'Change support ticket state',
      AUTH_LOGIN: 'Signed in',
      APPROVAL_REQUEST: 'Approval requested',
      APPROVAL_DECIDE: 'Approval decided',
      NOTIFY_CHANNEL_MESSAGE_RESENT: 'Message to a phone sent again',
      NOTIFY_CHANNEL_RESELLER_SAVED: "Zalo messages for a reseller's customers changed",
      NOTIFY_CHANNEL_TEMPLATE_DELETED: 'Notice no longer sent to Zalo',
      NOTIFY_CHANNEL_TEMPLATE_SAVED: 'Zalo template saved',
      NOTIFY_CHANNEL_TEMPLATE_TEST_SENT: 'Zalo test message sent',
      NOTIFY_PHONE_OPT_OUT_ADDED: 'Number added to the list that receives no messages',
      NOTIFY_PHONE_OPT_OUT_WITHDRAWN: 'Number removed from the list that receives no messages',
      ROLE_GRANT: 'Role granted',
      STAFF_USER_CREATE: 'Staff account created',
      STAFF_USER_PASSWORD_RESET: 'Staff password reset',
      CUSTOMER_CREATE: 'Customer created',
      CUSTOMER_STATUS: 'Customer status changed',
      RESELLER_CREATE: 'Reseller created',
      SUBJECT_CREATE: 'Registrant created',
      SUBJECT_NEW_VERSION: 'New registrant version',
      SUBJECT_VERIFICATION_COMPLETE: 'Verification completed',
      SUBJECT_VERIFICATION_REVOKE: 'Verification revoked',
      DOCUMENT_UPLOAD: 'Document uploaded',
      DOCUMENT_READ: 'Document viewed',
      IDENTITY_DOCUMENT_VIEW: 'Identity number revealed',
      IDENTITY_DOCUMENT_MACHINE_COMPARE: 'Identity document checked automatically',
      IDENTITY_DOCUMENT_REGISTRY_DISCLOSURE: 'Identity document sent to the registry',
      DOSSIER_COMPLETE: 'Dossier completed',
      DOMAIN_NAMESERVERS_UPDATE: 'Nameservers updated',
      DOMAIN_AUTHINFO_ISSUED: 'AuthInfo code issued',
      DOMAIN_LOCK_APPLY: 'Lock applied',
      DOMAIN_LOCK_RELEASE: 'Lock released',
      DOMAIN_TRANSFER_LOCK_SET: 'Transfer lock turned on',
      DOMAIN_TRANSFER_LOCK_CLEARED: 'Transfer lock turned off',
      DOMAIN_TRANSFER_OUT_REJECT: 'Transfer out rejected',
      DOMAIN_REGISTRANT_CHANGE_REQUEST: 'Registrant change requested',
      DOMAIN_REGISTRANT_CHANGE_CONFIRMED: 'Registrant change confirmed',
      DOMAIN_EXPIRY_CHECK: 'Expiry checked',
      REGISTRY_PAYLOAD_VIEW: 'Registry message viewed',
      BILLING_INVOICE_ISSUE: 'Invoice issued',
      BILLING_BANK_TRANSACTION_ASSIGN: 'Bank transaction assigned to a payment request',
      BILLING_BANK_TRANSACTION_CREDIT: 'Bank transaction credited to a wallet',
      BILLING_BANK_TRANSACTION_DISMISS: 'Bank transaction set aside',
      BILLING_BANK_TRANSACTION_SETTLEMENT_PAYOUT: 'Bank transaction marked as a gateway settlement',
      BILLING_PAYMENT_CONFIRM: 'Bank transfer confirmed by staff',
      BILLING_PAYMENT_REQUEST_CANCEL: 'Payment request cancelled',
      BILLING_PAYMENT_REQUEST_CREATE: 'Payment request (QR code) made',
      BILLING_PAYMENT_SESSION_REFUND: 'Refund to the payment source',
      BILLING_PAYMENT_SESSION_REFUND_RESOLVE: 'Refund to the payment source settled by staff',
      BILLING_PAYMENT_SESSION_SYNC: 'Gateway payment session status queried',
      BILLING_WALLET_ADJUST: 'Wallet balance adjusted',
      ABUSE_CASE_CREATE: 'Abuse case created',
      ABUSE_CASE_OPEN: 'Abuse case opened',
      ABUSE_REPORT_ATTACH: 'Abuse report added to a case',
      ABUSE_CASE_ASSIGN: 'Abuse case assigned',
      ABUSE_CASE_TRANSITION: 'Abuse case status changed',
      ABUSE_EVIDENCE_ADD: 'Evidence added',
      ABUSE_DOMAIN_ACTION: 'Abuse action on the domain',
      ABUSE_DOMAIN_ACTION_REQUEST: 'Abuse action requested',
      ABUSE_DOMAIN_ACTION_SETTLE: 'Abuse action completed',
      ABUSE_LAW_ENFORCEMENT_RECORD: 'Law-enforcement request recorded',
      ABUSE_LAW_ENFORCEMENT_UPDATE: 'Law-enforcement request updated',
      ABUSE_LE_RECORD: 'Law-enforcement request recorded',
      ABUSE_LE_UPDATE: 'Law-enforcement request updated',
      DISPUTE_OPEN: 'Dispute opened',
      DISPUTE_DECIDE: 'Dispute decided',
      DISPUTE_IMPLEMENT: 'Dispute decision carried out',
      DISPUTE_CLOSE: 'Dispute closed',
      DISCLOSURE_CASE_OPENED: 'Disclosure request opened',
      DISCLOSURE_REVIEW_STARTED: 'Disclosure review started',
      DISCLOSURE_INFO_REQUESTED: 'More information requested',
      DISCLOSURE_DECIDED: 'Disclosure decided',
      DISCLOSURE_RELEASED: 'Registration data released',
      DISCLOSURE_CLOSED: 'Disclosure request closed',
      AUCTION_CREATE: 'Auction created',
      AUCTION_CANCEL: 'Auction cancelled',
      AUCTION_DROP_SCHEDULE: 'Release date scheduled',
      AUCTION_RESULT_OVERRIDE: 'Auction result overridden',
      ESCROW_DEPOSIT_CREATED: 'Escrow deposit started',
      ESCROW_DEPOSIT_GENERATED: 'Escrow deposit generated',
      ESCROW_DEPOSIT_VALIDATED: 'Escrow deposit validated',
      ESCROW_DEPOSIT_VALIDATION_FAILED: 'Escrow deposit failed validation',
      ESCROW_DEPOSIT_ENCRYPTED: 'Escrow deposit encrypted',
      ESCROW_DEPOSIT_DELIVERY_STARTED: 'Escrow delivery started',
      ESCROW_DEPOSIT_DELIVERED: 'Escrow deposit delivered',
      ESCROW_DEPOSIT_DELIVERY_FAILED: 'Escrow delivery failed',
      ESCROW_DEPOSIT_DELIVERY_UNKNOWN: 'Escrow delivery result unknown',
      ESCROW_DEPOSIT_CONFIRMED: 'Escrow deposit confirmed by the agent',
      ESCROW_DEPOSIT_VERIFICATION_FAILED: 'Escrow agent could not verify the deposit',
      EINVOICE_DOCUMENT_CHANGE: 'E-invoice corrected, cancelled or dropped',
      EINVOICE_DOCUMENT_DOWNLOAD: 'E-invoice file downloaded',
      EINVOICE_DOCUMENT_ISSUE: 'E-invoice issued, sent again or looked up',
      ESCROW_DEPOSIT_ABANDONED: 'Escrow deposit abandoned',
      ESCROW_RECEIPT_RECORDED: 'Escrow receipt recorded',
      VNNIC_SYNC_SUBMIT: 'Dossier sent to VNNIC',
      VNNIC_SYNC_OUTCOME: 'VNNIC sync result recorded',
      VNNIC_SYNC_OUTCOME_RECORDED: 'VNNIC sync result recorded',
      VNNIC_SYNC_RESUBMIT: 'Dossier resent to VNNIC',
      VNNIC_SYNC_RESUBMIT_REQUESTED: 'Dossier resend to VNNIC requested',
      VNNIC_REPORT_GENERATE: 'VNNIC report generated',
      VNNIC_REPORT_GENERATED: 'VNNIC report generated',
      VNNIC_REPORT_EXPORT: 'VNNIC report exported',
      VNNIC_REPORT_EXPORTED: 'VNNIC report exported',
      VNNIC_REPORT_OUTCOME: 'VNNIC report result recorded',
      VNNIC_REPORT_OUTCOME_RECORDED: 'VNNIC report result recorded',
      VNNIC_REGISTRY_ENTERPRISE_LOOKUP: 'Business looked up through VNNIC',
      VNNIC_REGISTRY_BUSINESS_HOUSEHOLD_LOOKUP: 'Household business looked up through VNNIC',
      VNNIC_REGISTRY_VERIFICATION_LOOKUP: 'Verification looked up through VNNIC',
      VNNIC_REGISTRY_PROMOTION_CODE: 'VNNIC promotion code requested',
      INTEGRATION_CONNECTION_CREATE: 'Registry connection created',
      INTEGRATION_CONNECTION_UPDATE: 'Registry connection updated',
      INTEGRATION_CONNECTION_TEST: 'Connection tested',
      INTEGRATION_ENVIRONMENT_PROPOSE: 'Connection settings proposed',
      INTEGRATION_ENVIRONMENT_ACTIVATE: 'Connection settings activated',
      INTEGRATION_CREDENTIAL_PROPOSE: 'Credential proposed',
      INTEGRATION_CREDENTIAL_ACTIVATE: 'Credential activated',
      INTEGRATION_CREDENTIAL_REVOKE: 'Credential revoked',
      INTEGRATION_CERTIFICATE_PROPOSE: 'Certificate proposed',
      INTEGRATION_CERTIFICATE_ACTIVATE: 'Certificate activated',
      INTEGRATION_CERTIFICATE_REVOKE: 'Certificate revoked',
      INTEGRATION_SECRET_STORE: 'Secret stored',
      INTEGRATION_SECRET_DESTROY: 'Secret destroyed',
      INTEGRATION_TLD_CHANNEL_PROPOSE: 'TLD channel change proposed',
      INTEGRATION_TLD_CHANNEL_ACTIVATE: 'TLD channel change activated',
      INTEGRATION_PROVIDER_SETTING_PROPOSE: 'Provider setting proposed',
      INTEGRATION_PROVIDER_SETTING_ACTIVATE: 'Provider setting activated',
      INTEGRATION_MODULE_INSTANCE_ACTION: 'Module action run',
      INTEGRATION_MODULE_INSTANCE_ACTIVATE: 'Module settings applied',
      INTEGRATION_MODULE_INSTANCE_CREATE: 'Module connection added',
      INTEGRATION_MODULE_INSTANCE_DISABLE: 'Module connection switched off',
      INTEGRATION_MODULE_INSTANCE_DRAFT: 'Module settings draft saved',
      INTEGRATION_MODULE_INSTANCE_ENABLE: 'Module connection switched on',
      INTEGRATION_MODULE_INSTANCE_PROPOSE: 'Module settings sent for approval',
      INTEGRATION_MODULE_INSTANCE_TEST: 'Module connection tested',
      ADDON_INSTALL: 'Add-on installed',
      ADDON_ACTIVATE: 'Add-on switched on',
      ADDON_DEACTIVATE: 'Add-on switched off',
      ADDON_UNINSTALL: 'Add-on uninstalled',
      ADDON_CIRCUIT_OPEN: 'Add-on stopped after repeated failures',
      ADDON_EVENT_REPLAY: 'Event sent to an add-on again',
      ADDON_ACTION: 'Action on an add-on page',
      INTEGRATION_SPONSORING_REGISTRAR_PROPOSE: 'Sponsoring registrar proposed',
      INTEGRATION_SPONSORING_REGISTRAR_ACTIVATE: 'Sponsoring registrar activated',
      INTEGRATION_CHANGE_APPLY: 'Change applied',
      INTEGRATION_CHANGE_CANCEL: 'Change cancelled',
      OPS_INCIDENT_OPEN: 'Incident opened',
      OPS_INCIDENT_OPENED: 'Incident opened',
      OPS_INCIDENT_TRANSITION: 'Incident status changed',
      AUTH_LOGOUT: 'Signed out',
      ACCESS_CHANGE_PROPOSE: 'Access change proposed',
      ACCESS_CHANGE_APPLY: 'Access change applied',
      ACCESS_CHANGE_CANCEL: 'Access change cancelled',
      API_CREDENTIAL_ISSUE: 'API key issued',
      API_CREDENTIAL_REVOKE: 'API key revoked',
      CUSTOMER_CONTACT_SHARING: 'Contact sharing with the CRM changed',
      API_CREDENTIAL_EXPIRY: 'API key expiry shortened',
      REPORTING_CONSUMER_CREATE: 'Place that receives data added',
      REPORTING_CONSUMER_UPDATE: 'Place that receives data renamed',
      REPORTING_CONSUMER_CHANGE: 'Data received by a place changed',
      REPORTING_CHECKPOINT_RESET: 'Reporting data sent again from the start',
      REPORTING_KEY_ROTATE: 'Reporting key replaced',
      REPORTING_KEY_SUSPEND: 'Reporting key suspended',
      REPORTING_KEY_RESUME: 'Reporting key unlocked',
      REPORTING_KEY_REVOKE: 'Reporting key revoked',
      REPORTING_PARKED_RESEND: 'Row not received sent again',
      WEBHOOK_REPORTING_ENDPOINT_SAVE: 'Change signal address saved',
      WEBHOOK_REPORTING_ENDPOINT_DISABLE: 'Change signal address removed',
      APPROVAL_SELF_APPROVE: 'Self-approved',
      ROLE_REVOKE: 'Role revoked',
      STAFF_USER_DISABLE: 'Staff account disabled',
      STAFF_USER_ENABLE: 'Staff account enabled',
      STAFF_USER_MFA_RESET: 'Staff MFA reset',
      STAFF_USER_UNLOCK: 'Staff account unlocked',
      CUSTOMER_UPDATE: 'Customer details changed',
      CUSTOMER_USER_ADD: 'User added to customer',
      CUSTOMER_USER_CREATE: 'Customer user created',
      AUCTION_OPEN: 'Auction opened',
      AUCTION_CLOSE: 'Auction closed',
      BACKORDER_STAFF_CANCEL: 'Backorder cancelled by staff',
      COMPLIANCE_WORK_ITEM_OPEN: 'Work item opened',
      COMPLIANCE_WORK_ITEM_ASSIGN: 'Work item assigned',
      COMPLIANCE_WORK_ITEM_RESOLVE: 'Work item resolved',
      COMPLIANCE_WORK_ITEM_AUTO_RESOLVE: 'Work item resolved automatically',
      DISPUTE_REGISTRY_LOCK: 'Registry lock for dispute',
      DISPUTE_REGISTRY_UNLOCK: 'Registry lock for dispute released',
      DNS_LOGIN_RECORDS_EDIT: 'DNS records edited through the domain DNS login',
      DNS_LOGIN_SIGN_IN: 'Domain DNS login sign-in',
      DNS_ZONE_IMPORT: 'DNS zone imported from a zone file',
      DNS_ZONE_LOGIN_REVOKE: 'Domain DNS login revoked',
      DNS_ZONE_LOGIN_SET: 'Domain DNS login password set',
      DNS_ZONE_RECORDS_EDIT: 'DNS records edited by staff',
      DNS_ZONE_ROLLBACK: 'DNS zone back to an earlier version',
      DNS_ZONE_TEMPLATE_APPLY: 'DNS zone template applied',
      DNS_ZONE_TSIG_KEY_SET: 'DNS zone copy key (TSIG) set',
      DOMAIN_STAFF_REGISTER: 'Domain registered by staff',
      DOMAIN_STAFF_AUTH_INFO: 'Transfer code issued by staff',
      DOMAIN_STAFF_DELETE: 'Domain deleted by staff',
      DOMAIN_STAFF_DNSSEC: 'DNSSEC changed by staff',
      DOMAIN_STAFF_NAMESERVERS: 'Name servers changed by staff',
      DOMAIN_STAFF_REGISTRANT_CHANGE: 'Registrant change requested by staff',
      DOMAIN_STAFF_REGISTRANT_CHANGE_CONFIRM: 'Registrant change confirmed by staff',
      DOMAIN_STAFF_RENEW: 'Domain renewed by staff',
      DOMAIN_STAFF_RENEWAL_OVERRIDE: 'Staff confirmed another renewal within 24 hours',
      DOMAIN_STAFF_TRANSFER_CANCEL: 'Transfer cancelled by staff',
      DOMAIN_STAFF_TRANSFER_LOCK: 'Transfer lock changed by staff',
      DOMAIN_STAFF_UPDATE_LOCK: 'Update lock changed by staff',
      DOMAIN_STAFF_CLIENT_HOLD: 'Domain hold (clientHold) changed by staff',
      DOMAIN_TRANSFER_IN_CANCEL: 'Transfer in cancelled',
      ESCROW_DEPOSIT_OBJECTS_PURGED: 'Expired escrow files purged',
      INTEGRATION_OTE_EXERCISE_START: 'OT&E exercise started',
      INTEGRATION_OTE_EXERCISE_FINISH: 'OT&E exercise finished',
      INTEGRATION_BALANCE_READ: 'Registry balance read',
      INTEGRATION_ACCOUNT_DOMAINS_READ: 'Registry domain list read',
      INTEGRATION_CREDENTIAL: 'Registry credential action',
      INTEGRATION_CERTIFICATE: 'Registry certificate action',
      INTEGRATION_MANAGE: 'Integration change',
      NOTIFY_NOTIFICATION_RESENT: 'Notification resent',
      NOTIFY_SETTINGS_SAVED: 'Notification settings saved',
      NOTIFY_SETTINGS_TEST: 'Notification test sent',
      NOTIFY_TEMPLATE_SAVED: 'Notification template saved',
      NOTIFY_TEMPLATE_REVERTED: 'Notification template reverted',
      PRICING_BOOK_CREATE: 'Price book created',
      PRICING_BOOK_STATUS: 'Price book status changed',
      PRICING_PROMOTION_CREATE: 'Promotion created',
      PRICING_PROMOTION_UPDATE: 'Promotion updated',
      PRICING_PROMOTION_DISABLE: 'Promotion disabled',
      PRICING_PROMOTION_SCOPE: 'Promotion scoped to a group, product or plan',
      CATALOG_GROUP_SAVE: 'Product group saved',
      CATALOG_PRODUCT_SAVE: 'Product saved',
      CATALOG_PRODUCT_VISIBILITY: 'Product shown, hidden or retired',
      CATALOG_PLAN_SAVE: 'Plan saved',
      CATALOG_PLAN_PUBLISH: 'Plan put on sale or taken off',
      CATALOG_FULFILMENT_SET: 'Plan delivery set',
      CATALOG_STOCK_SET: 'Plan stock set',
      SUBSCRIPTION_CANCEL_DELIVERY: 'Manual service could not be delivered',
      SUBSCRIPTION_COMPLETE_DELIVERY: 'Manual service delivery completed',
      SUBSCRIPTION_EXTEND: 'Service extended',
      SUBSCRIPTION_ORDER: 'New service ordered for a customer',
      SUBSCRIPTION_SUSPEND: 'Service suspended',
      SUBSCRIPTION_TERMINATE: 'Service termination requested',
      SUBSCRIPTION_UNSUSPEND: 'Service unsuspended',
      HOSTING_ACCOUNT_AUTOSSL: 'AutoSSL run for a hosting account',
      HOSTING_ACCOUNT_PASSWORD_SET: 'cPanel password set',
      HOSTING_ACCOUNT_REFRESH: 'Hosting account synced from the server',
      HOSTING_ACCOUNT_SIGN_IN: 'Signed in to cPanel for the customer',
      HOSTING_OPERATION_RESOLVE: 'Provisioning operation with an unknown result resolved',
      HOSTING_SERVER_ACCEPTING: 'New accounts on a hosting server switched on or off',
      HOSTING_SERVER_REFRESH: 'Hosting servers read again from the panel',
      CATALOG_CROSS_SELLS_SET: 'Items offered alongside set',
      CATALOG_FREE_DOMAINS_SET: 'Free domains of a plan set',
      CATALOG_OPTION_SAVE: 'Configuration option saved',
      CATALOG_ADDON_SAVE: 'Add-on saved',
      CATALOG_LOCATION_SAVE: 'Location (data centre) saved',
      CATALOG_PLACEMENT_GROUP_SAVE: 'Server group saved',
      CATALOG_LIFECYCLE_SET: 'Overdue settings set (renewal, suspension, termination)',
      CATALOG_PLAN_PRICE_PUBLISH: 'Plan prices per cycle published',
      CATALOG_PLAN_PRICE_END: 'Plan price ended',
      CATALOG_OPTION_PRICE_PUBLISH: 'Configuration option prices published',
      CATALOG_ADDON_PRICE_PUBLISH: 'Add-on prices published',
      CATALOG_PRICE_RULE_PUBLISH: 'Tier or customer price discount published',
      CATALOG_PRICE_RULE_END: 'Tier or customer price discount ended',
      PRICING_REGISTRY_PRICE_PUBLISH: 'Registry price published',
      PRICING_REGISTRY_PRICE_END: 'Registry price ended',
      PRICING_RULE_PUBLISH: 'Price rule published',
      PRICING_TAX_DEFAULT_SET: 'Default VAT code set',
      PRICING_TAX_RATE_END: 'VAT rate ended',
      PRICING_TAX_RATE_PUBLISH: 'VAT rate published',
      PRICING_RULE_END: 'Price rule ended',
      REFERENCE_VN_PLACES_SYNC: 'Place list synchronised',
      REGISTRY_POLL_ACK: 'Registry poll message acknowledged',
      REGISTRY_POLL_PROCESSED: 'Registry poll message processed',
      REGISTRY_RECONCILIATION_RESOLVE: 'Reconciliation issue resolved',
      WEBHOOK_DELIVERY_RESEND: 'Webhook delivery resent',
      WEBHOOK_ENDPOINT_SAVE: 'Webhook endpoint saved',
      WEBHOOK_ENDPOINT_TEST: 'Webhook endpoint tested',
      VNNIC_REGISTRY_VERIFICATION_LOOKUP_DENIED: 'VNNIC verification lookup refused',
      OPS_INCIDENT_ACKNOWLEDGE: 'Incident acknowledged',
      ADMIN_USERS_MANAGE: 'Staff management',
      AUDIT_READ: 'Audit log read',
      COMPLIANCE_READ: 'Compliance data read',
      DOSSIER_READ: 'Dossier read',
      ACCOUNT_USER_INVITE_ACCEPT: 'Account invitation accepted',
      AUTH_ACCOUNT_SWITCH: 'Switched account',
      AUTH_CAPTCHA_FAILED: 'Anti-robot check not passed',
      AUTH_DEMO_LOGIN: 'Quick sign-in (OT&E)',
      AUTH_PASSWORD_RESET: 'Password reset',
      CLIENT_USER_EMAIL_VERIFY: 'Client user e-mail verified',
      CLIENT_USER_SIGN_UP: 'Client user signed up',
      COMPLIANCE_POST_OPERATION_BACKFILL: 'Post-operation backfill',
      COMPLIANCE_REGISTRAR_SIGNATURE_SETTINGS: 'Registrar signature settings changed',
      CUSTOMER_IDENTITY_CARRIED: 'Customer identity carried over',
      CUSTOMER_PRIMARY_SUBJECT_SET: 'Customer primary subject set',
      DECLARATION_CHECK_SETTINGS_UPDATE: 'Declaration check settings changed',
      DECLARATION_GENERATE: 'Declaration generated',
      DECLARATION_REFUSED: 'Declaration refused',
      DECLARATION_UPLOAD: 'Declaration uploaded',
      DECLARATION_UPLOAD_BY_STAFF: 'Declaration uploaded by staff',
      ACCOUNT_USER_INVITE: 'User invited to the account',
      AUCTION_BID: 'Auction bid placed',
      AUTH_LOGIN_FAILED: 'Sign-in failed',
      AUTH_STEP_UP: 'Re-verified with a code',
      BACKORDER_CANCEL: 'Backorder cancelled',
      BACKORDER_CREATE: 'Backorder placed',
      COMPLIANCE_REGISTRAR_SIGNATURE_AUTO_ITEM_SATISFIED: 'Automatic declaration covered by a manual registrar signature',
      COMPLIANCE_REGISTRAR_SIGNATURE_BATCH_OPENED: 'Registrar signing batch opened',
      COMPLIANCE_REGISTRAR_SIGNATURE_BATCH_SENT: 'Registrar signing batch sent',
      COMPLIANCE_REGISTRAR_SIGNATURE_DECLARATION_ACCEPTED: 'Staff approved SmartCA signing of a domain declaration',
      COMPLIANCE_REGISTRAR_SIGNATURE_DECLARATION_BACKFILLED: 'Registrar confirmation recorded for an earlier signed declaration',
      COMPLIANCE_REGISTRAR_SIGNATURE_FILE_REQUESTED: 'Dossier file sent for registrar signature',
      COMPLIANCE_REGISTRAR_SIGNATURE_ITEM_CANCELLED: 'Registrar signature request cancelled (domain not created)',
      DOCUMENT_CONTENT: 'Document content viewed',
      DOMAIN_ADDON_APPLIED: 'Add-on applied',
      DOMAIN_ADDON_CANCELLED: 'Add-on cancelled',
      DOMAIN_ADDON_ENDED: 'Add-on ended',
      DOMAIN_ADDON_ORDERED: 'Add-on ordered',
      DOMAIN_ADDON_UNLOCK_REQUESTED: 'Add-on unlock requested',
      DOMAIN_EXTRA_FIELDS_EDIT: 'Domain extra fields edited',
      DOMAIN_FULL_LOCK_APPLIED: 'Full lock applied',
      DOMAIN_CLIENT_STATUSES_CHANGED: 'Protective registry statuses changed',
      DOMAIN_NAME_CHANGE_CANCELLED: 'Name change cancelled',
      DOMAIN_NAME_CHANGE_COMPLETED: 'Name change completed',
      DOMAIN_NAME_CHANGE_CONSENT: 'Name change consent recorded',
      DOMAIN_NAME_CHANGE_DECLARATION: 'Name change declaration',
      DOMAIN_NAME_CHANGE_OPENED: 'Name change opened',
      DOMAIN_NAME_CHANGE_REVIEWED: 'Name change reviewed',
      DOMAIN_NAME_CHANGE_SUBMITTED: 'Name change submitted',
      DOMAIN_NAME_CHANGE_VNNIC_REFUSED: 'Name change refused by VNNIC',
      DOMAIN_PANEL_OPERATION_COMPLETED: 'VNNIC panel step completed',
      DOMAIN_PANEL_OPERATION_OPEN: 'VNNIC panel step opened',
      DOMAIN_PANEL_OPERATION_SUBMITTED: 'VNNIC panel step submitted',
      DOMAIN_PROTECTED_CHANGE_PENDING: 'Protected domain change pending',
      DOMAIN_STAFF_NAME_CHANGE_CONSENT: 'Staff recorded name change consent',
      DOMAIN_STAFF_NAME_CHANGE_DECLARATION: 'Staff uploaded a name change declaration',
      DOMAIN_STAFF_NAME_CHANGE_DOCUMENT: 'Staff uploaded a name change document',
      DOMAIN_STAFF_NAME_CHANGE_OPEN: 'Staff opened a name change',
      DOMAIN_STAFF_PANEL_SUBMITTED: 'Staff recorded a VNNIC panel step',
      DOMAIN_STAFF_RESTORE: 'Staff restore',
      DOMAIN_STAFF_TRANSFER_IN: 'Staff transfer in',
      DOMAIN_UPSTREAM_VERIFICATION_RESENT: 'Upstream verification e-mail resent',
      DOSSIER_CASE: 'Dossier case recorded',
      DOSSIER_CASE_BY_STAFF: 'Dossier case recorded by staff',
      DOSSIER_DOCUMENT_RECLASSIFIED: 'Dossier document reclassified',
      DOSSIER_DOCUMENT_TYPE_UPDATE: 'Dossier document type changed',
      DOSSIER_FILE_UPLOAD: 'Dossier file uploaded',
      DOSSIER_FILE_UPLOAD_BY_STAFF: 'Dossier file uploaded by staff',
      DOSSIER_NOTE_ADD: 'Dossier note added',
      DOSSIER_REGISTRAR_ATTESTED: 'Dossier attested by the registrar',
      DOSSIER_REGISTRAR_SIGNED_FILE: 'Dossier file signed by the registrar',
      DOSSIER_REMINDER_SEND: 'Dossier reminder sent',
      MFA_ENROL: 'MFA enrolled',
      MFA_RESET: 'MFA reset',
      NAME_CHANGE_REQUIREMENTS_PUBLISH: 'Name change requirements published',
      REGISTRY_TLD_DEFAULT_CONNECTION: 'TLD default connection changed',
      REGISTRY_TLD_DISABLE: 'TLD disabled',
      REGISTRY_TLD_ENABLE: 'TLD enabled',
      REGISTRY_TLD_PUBLICATION: 'TLD publication changed',
      REGISTRY_TLD_SETUP: 'TLD set up',
      SUBJECT_DECLARATION_CARRIED: 'Subject declaration carried over',
      SUBJECT_LEGAL_REPRESENTATIVE_SET: 'Legal representative set',
      VNNIC_DOMAIN_TRACKING_REPORT_SEND: 'Daily domain tracking report sent',
      VNNIC_DOSSIER_FILE_READ: 'VNNIC dossier file read',
      VNNIC_PROMO_CODE_REJECTED: 'VNNIC promotion code rejected',
      VNNIC_PROMO_CODE_REQUEST: 'VNNIC promotion code requested',
      VNNIC_PROMO_CODE_RESERVE: 'VNNIC promotion code reserved',
      VNNIC_PROMO_CODE_USED: 'VNNIC promotion code used',
      VNNIC_REGISTRY_LIST_CHECK: 'VNNIC domain list compared',
      DOMAIN_CONTACTS_CHANGED: 'Domain contacts updated',
      DOMAIN_DELETE_REQUEST: 'Domain deletion requested',
      DOMAIN_DNSSEC_CHANGE: 'DNSSEC changed',
      DOMAIN_NAMESERVERS_CHANGE: 'Nameservers changed',
      DOMAIN_PARKING_PARKED: 'Switched to the parking nameservers at expiry',
      DOMAIN_PARKING_UNPARKED: 'Previous nameservers restored after renewal',
      DOMAIN_PROTECTED_CHANGE_CONFIRMED: 'Change on a protected domain confirmed',
      DOMAIN_PROTECTED_CHANGE_REJECTED: 'Change on a protected domain rejected',
      DOMAIN_PROTECTION_REMOVED: 'Domain protection removed',
      DOMAIN_PROTECTION_SET: 'Domain protection set',
      DOMAIN_REGISTER: 'Domain registered',
      DOMAIN_REGISTRATION_CANCEL: 'Staff cancelled the registration',
      DOMAIN_REGISTRATION_REACTIVATE: 'Staff reactivated the registration order',
      DOMAIN_REGISTRATION_RETRY: 'Staff resumed the registration',
      DOMAIN_RENEW: 'Domain renewed',
      DOMAIN_RESTORE: 'Domain restored',
      DOMAIN_STAFF_ADDON_APPLY: 'Staff recorded an add-on as carried out',
      DOMAIN_STAFF_ADDON_CANCEL: 'Staff cancelled an add-on',
      DOMAIN_STAFF_ADDON_ORDER: 'Staff ordered an add-on',
      DOMAIN_STAFF_NAME_CHANGE_CANCEL: 'Staff cancelled a name change',
      DOMAIN_STAFF_NAME_CHANGE_CONFIRM_COMPLETION: 'Staff confirmed a name change as completed',
      DOMAIN_STAFF_NAME_CHANGE_REVIEW: 'Staff reviewed a name change',
      DOMAIN_STAFF_NAME_CHANGE_SUBMIT: 'Staff submitted a name change',
      DOMAIN_STAFF_NAME_CHANGE_VNNIC_REFUSED: 'Staff recorded VNNIC\'s refusal of a name change',
      DOMAIN_STAFF_PANEL_CHECK: 'Staff checked a VNNIC system change',
      DOMAIN_STAFF_PANEL_REFUSED: 'Staff recorded the registry\'s refusal of a lock / unlock',
      DOMAIN_STAFF_TRANSFER_OUT_RESPOND: 'Staff answered a transfer out',
      DOMAIN_STAFF_UPSTREAM_REFRESH: 'Staff refreshed the state from the upstream registrar',
      DOMAIN_STAFF_REGISTRY_SYNC: 'Staff synced the domain with the registry',
      DOMAIN_STAFF_IMPORT: 'Staff brought the name in from the registry',
      DOMAIN_STAFF_IMPORT_ALL: 'Staff brought every name of the provider into the platform',
      SUBJECT_CUSTOMER_ASSIGN: 'Subject assigned to a customer',
      DOMAIN_STAFF_REGISTRY_PREVIEW: 'Staff viewed the name at the registry',
      DOMAIN_STAFF_VERIFICATION_RESENT: 'Staff resent the verification e-mail',
      DOMAIN_STAFF_EMAIL_SENT: 'Staff sent a templated e-mail',
      DOMAIN_STAFF_REGISTRANT_CONTACT: 'Staff created a new registrant at the registry',
      DOMAIN_STAFF_REGISTRY_CONTACTS: 'Staff viewed the contacts at the registry',
      DOMAIN_TRANSFER_IN_START: 'Transfer in started',
      DOMAIN_TRANSFER_OUT_RESPOND: 'Transfer out answered',
      INTEGRATION_CERTIFICATE_IMPORT_PKCS12: 'Certificate imported from a .p12 file',
      INTEGRATION_CONNECTION_ROLE: 'Connection registrar role changed',
      OPS_INCIDENT_ACKNOWLEDGED: 'Incident acknowledged',
      OPS_INCIDENT_MITIGATING: 'Incident mitigation started',
      OPS_INCIDENT_MONITORING: 'Incident under monitoring',
      OPS_INCIDENT_RESOLVED: 'Incident resolved',
      OPS_LOG_RETENTION_PURGE: 'Old logs deleted from the log store',
      OPS_LOG_RETENTION_SETTINGS: 'Log retention changed',
      PRICING_ADDON_CLEANUP: 'Add-on data cleaned up',
      PRICING_ADDON_CLEANUP_REVERT: 'Add-on data clean-up reverted',
      PRICING_ADDON_TLD_APPLY_DEFAULTS: 'Default add-ons turned on for the TLD',
      PRICING_ADDON_TLD_SET: 'TLD add-on mode set',
      PRICING_COST_COMPONENT_END: 'Upstream fee component ended',
      PRICING_COST_COMPONENT_PUBLISH: 'Upstream fee component published',
      TLD_DEFAULT_NAMESERVERS_SET: 'Default registration nameservers updated',
      TLD_PARKING_NAMESERVERS_SET: 'Parking nameservers updated',
      TLD_SETTINGS_PUBLISH: 'TLD settings published',
    },
    /** Where an event came from, or which automatic process acted (service names are never shown). */
    source: {
      ADMIN_API: 'Control Center',
      API: 'Customer portal or API integration',
      WORKER: 'Automatic processing',
      DOMAIN_WORKER: 'Automatic processing',
      LIFECYCLE: 'Automatic processing',
      LIFECYCLE_WORKER: 'Automatic processing',
      JOB_WORKER: 'Automatic processing',
      SCHEDULER: 'Scheduled processing',
      EPP_POLL_WORKER: 'Registry notice',
      EPP_GATEWAY: 'Registry connection',
      RECONCILIATION: 'Registry reconciliation',
      RECONCILIATION_WORKER: 'Registry reconciliation',
      DOSSIER: 'Dossier processing',
      CASES: 'Compliance case handling',
      VNNIC_SYNC_WORKER: 'VNNIC sync',
      VNNIC_RECONCILIATION_WORKER: 'VNNIC reconciliation',
      VNNIC_REPORT_WORKER: 'VNNIC reporting',
      IDENTITY_WORKER: 'Identity verification',
      DROPCATCH_WORKER: 'Drop catching',
      OBSERVABILITY_API: 'Monitoring',
      DEV_SEED: 'Sample data',
    },
    epp: {
      DOMAIN_CHECK: 'Domain check',
      DOMAIN_INFO: 'Domain info',
      DOMAIN_CREATE: 'Domain create',
      DOMAIN_RENEW: 'Domain renew',
      DOMAIN_UPDATE: 'Domain update',
      DOMAIN_UPDATE_STATUS: 'Domain status update',
      DOMAIN_UPDATE_REGISTRANT: 'Registrant update',
      DOMAIN_UPDATE_AUTHINFO: 'AuthInfo update',
      DOMAIN_DELETE: 'Domain delete',
      DOMAIN_RESTORE: 'Domain restore',
      DOMAIN_DISPUTE: 'Domain dispute',
      DOMAIN_TRANSFER_REQUEST: 'Transfer request',
      DOMAIN_TRANSFER_QUERY: 'Transfer query',
      DOMAIN_TRANSFER_APPROVE: 'Transfer approve',
      DOMAIN_TRANSFER_REJECT: 'Transfer reject',
      DOMAIN_TRANSFER_CANCEL: 'Transfer cancel',
      CONTACT_CHECK: 'Contact check',
      CONTACT_INFO: 'Contact info',
      CONTACT_CREATE: 'Contact create',
      CONTACT_UPDATE: 'Contact update',
      CONTACT_DELETE: 'Contact delete',
      CONTACT_TRANSFER: 'Contact transfer',
      HOST_CHECK: 'Host check',
      HOST_INFO: 'Host info',
      HOST_CREATE: 'Host create',
      HOST_UPDATE: 'Host update',
      HOST_DELETE: 'Host delete',
      POLL_REQUEST: 'Poll request',
      POLL_ACK: 'Poll acknowledge',
      LOGIN: 'Login',
      LOGOUT: 'Logout',
      HELLO: 'Hello',
    },
    order: {
      withOthers: '{{domain}} +{{n}} · {{date}}',
      single: '{{domain}} · {{date}}',
      dateOnly: 'Order {{date}}',
    },
  },
  range: {
    label: 'Time range',
    last: 'Last {{value}}',
    custom: 'Custom',
    from: 'From (UTC)',
    to: 'To (UTC)',
    presets: { '5m': '5 minutes', '15m': '15 minutes', '1h': '1 hour', '6h': '6 hours', '24h': '24 hours', '7d': '7 days', '30d': '30 days' },
    invalid: 'Start and end must be valid dates.',
    order: 'End must be after start.',
    tooLong: 'At most {{days}} days per query.',
  },
} as const;
